import Link from "@components/Link/Link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import { NextContentfulImage } from "../components/Image";

const styleRegEx = /style="([^"]*rem[^"]*)"/g;
const fontSizeRegEx = /(font-size\s*:\s*)(\d*\.?\d+)px/g;
const remRegEx = /(\d*\.?\d+)rem/g;

function decreaseRemValues(styleString: string): string {
  // 1. Convert all rem values to px
  let result = styleString.replace(remRegEx, (_, value) => {
    const val = parseFloat(value);
    if (isNaN(val)) return `${value}rem`;

    const transformedToPx = val * 10;

    return `${transformedToPx}px`;
  });

  // 2. Convert font-size back to rem values
  result = result.replace(fontSizeRegEx, (_, prefix, value) => {
    const val = parseFloat(value);
    if (isNaN(val)) return `${prefix}${value}rem`;

    const transformedToRems = (val / 16).toFixed(4).replace(/\.?0+$/, "");

    return `${prefix}${transformedToRems}rem`;
  });

  return result;
}

export function transformStylesInRichText(
  htmlString: string | undefined
): string {
  if (typeof htmlString !== "string") {
    // if input isn't string, return as-is or empty string
    return htmlString ?? "";
  }

  // Find all style="..." occurrences containing 'rem' and update rem values inside
  return htmlString.replace(styleRegEx, (match, styleContent) => {
    if (typeof styleContent !== "string") {
      // skip replacement if no style content captured
      return match;
    }
    const newStyle = decreaseRemValues(styleContent);
    return `style="${newStyle}"`;
  });
}

const sharedRenderNode = {
  [INLINES.ENTRY_HYPERLINK]: (node, text) => {
    const { slug } = node.data.target.fields;
    return <Link href={`/${slug}`}>{text}</Link>;
  },
  [INLINES.HYPERLINK]: (node, renderedValues) => {
    return (
      <Link href={node.data.uri} target="_blank" rel="noreferrer">
        {renderedValues}
      </Link>
    );
  },
};

const sharedRenderMark = {
  [MARKS.CODE]: (text) => <span className="highlight">{text}</span>,
};

export default function richTextify(richText) {
  return documentToReactComponents(richText, {
    renderNode: {
      ...sharedRenderNode,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        return (
          <div
            style={{
              maxWidth: `${node.data.target.fields.file.details.image.width}px`,
            }}
            className="rich-text-img"
          >
            <NextContentfulImage
              src={node.data.target.fields.file.url}
              alt={node.data.target.fields.title}
              width={node.data.target.fields.file.details.image.width}
              height={node.data.target.fields.file.details.image.height}
            />
          </div>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node) => {
        const { title, file } = node.data.target.fields;
        const { url } = file;
        return (
          <Link href={url} target="_blank" rel="noreferrer">
            {title}
          </Link>
        );
      },
    },
    renderMark: {
      ...sharedRenderMark,
    },
  });
}

// rich text renderer that removes <p> around text nodes
export function richTextifyEnhanced(richTextDocument) {
  const options = {
    renderNode: {
      ...sharedRenderNode,
      [BLOCKS.PARAGRAPH]: (_, children) => children,
    },
    renderMark: {
      ...sharedRenderMark,
    },
  };
  return documentToReactComponents(richTextDocument, options);
}
